import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, SearchBar, Spinner, Pagination } from './shared';
import Modal from './shared/Modal';
import { showSuccess, showError } from '../../utils/toastUtils';

const STATUS_TONE = {
    new: 'bg-gray-100 text-gray-700 border-gray-200',
    content_ready: 'bg-amber-50 text-amber-700 border-amber-200',
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    unpublished: 'bg-gray-100 text-gray-600 border-gray-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
};

function StatusBadge({ status }) {
    const value = status || 'new';
    const tone = STATUS_TONE[value] || 'bg-gray-100 text-gray-700 border-gray-200';
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${tone}`}>
            {value}
        </span>
    );
}

const PAGE_SIZE = 20;

export default function AdminInstrumentIntelligence() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => { loadItems(); }, [offset]);

    const loadItems = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listInstrumentsWithInsights({
                query: searchQuery.trim() || undefined,
                offset,
                limit: PAGE_SIZE,
            });
            setItems(res.items || []);
            setTotal(res.total || 0);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load insights');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (offset !== 0) setOffset(0);
        else loadItems();
    };

    const detailPath = (item) => `/admin/instrument-intelligence/insights/${encodeURIComponent(item.id)}`;
    const publicDetailUrl = (item) => `${window.location.origin}/insights/${encodeURIComponent(item.document_id || item.id)}`;

    const openDetail = (item) => {
        navigate(detailPath(item));
    };

    const handleCopyLink = async (e, item) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(publicDetailUrl(item));
            showSuccess('Đã sao chép link bài viết');
        } catch (err) {
            showError('Không sao chép được link: ' + (err.message || ''));
        }
    };

    const handleOpenNewTab = (e, item) => {
        e.stopPropagation();
        window.open(publicDetailUrl(item), '_blank', 'noopener');
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await adminApi.deleteInstrumentInsight(deleteTarget.id);
            setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
            showSuccess(`Đã xóa "${deleteTarget.title || deleteTarget.document_id}"`);
            setDeleteTarget(null);
        } catch (err) {
            showError(err.message || 'Xóa thất bại');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Instrument Insights</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {items.length} insight{items.length !== 1 ? 's' : ''}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => navigate('/admin/instrument-intelligence/ingest')}>
                        Ingest Insight
                    </Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                onSearch={handleSearch}
                onRefresh={handleSearch}
                placeholder="Tìm theo title, summary, content..."
                className="mb-4"
            />

            {loading ? (
                <Spinner size="lg" text="Loading insights..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">
                    No insights published yet.
                </div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500 w-12">#</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Title</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Canonical ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Asset</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Category</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Ver</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Status</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Views</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Snapshot</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((it, idx) => (
                                <tr
                                    key={it.id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => openDetail(it)}
                                >
                                    <td className="px-3 py-3 text-center text-xs text-gray-400 tabular-nums">{idx + 1}</td>
                                    <td className="px-4 py-3 max-w-[420px]">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-gray-900 truncate flex-1" title={it.title}>
                                                {it.title || <span className="text-gray-400 italic">(no title)</span>}
                                            </span>
                                            <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={(e) => handleCopyLink(e, it)}
                                                    title="Copy link bài viết"
                                                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={(e) => handleOpenNewTab(e, it)}
                                                    title="Mở bài viết ở tab mới"
                                                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 font-mono text-xs text-gray-600">{it.canonical_id}</td>
                                    <td className="px-3 py-3">
                                        <Badge variant="outline">{it.asset_class}</Badge>
                                    </td>
                                    <td className="px-3 py-3 text-gray-600">{it.category}</td>
                                    <td className="px-3 py-3 text-center font-mono text-xs">v{it.version}</td>
                                    <td className="px-3 py-3">
                                        <StatusBadge status={it.status} />
                                    </td>
                                    <td className="px-3 py-3 text-right font-mono text-xs tabular-nums text-gray-700">
                                        {Number(it.view_count || 0).toLocaleString('vi-VN')}
                                    </td>
                                    <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                                        {it.snapshot_at ? new Date(it.snapshot_at).toLocaleString() : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="xs" onClick={() => openDetail(it)}>
                                                View
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                className="text-red-600 hover:bg-red-50"
                                                onClick={() => setDeleteTarget(it)}
                                            >
                                                Xóa
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 pb-3">
                        <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
                    </div>
                </div>
            )}

            <Modal
                open={!!deleteTarget}
                onClose={() => !deleting && setDeleteTarget(null)}
                title="Xóa vĩnh viễn bài viết này?"
                size="sm"
            >
                {deleteTarget && (
                    <>
                        <Modal.Body>
                            <p className="text-sm text-gray-700">
                                Hành động này <span className="font-semibold text-red-600">không thể hoàn tác</span>.
                                Toàn bộ lượt xem, bucket thống kê và pointer phiên bản sẽ bị xóa cùng.
                            </p>
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-1.5">
                                {deleteTarget.title && (
                                    <div className="text-sm font-semibold text-gray-900 break-words">
                                        {deleteTarget.title}
                                    </div>
                                )}
                                <div className="text-xs font-mono text-gray-600 break-all">
                                    {deleteTarget.document_id || deleteTarget.id}
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                Huỷ
                            </Button>
                            <Button variant="danger" size="sm" onClick={handleDelete} loading={deleting}>
                                Xóa vĩnh viễn
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </div>
    );
}
